import React, { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { api, formatApiError, asItems } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

export default function SavedProperties() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    api.get("/saved").then(({ data }) => setItems(asItems(data)))
      .catch((requestError) => {
        const message = formatApiError(requestError);
        setItems([]);
        setError(message);
        toast.error(message);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    try { await api.delete(`/saved/${id}`); toast.success("Removed from saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="saved-properties-page">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">My Saved</p>
      <h1 className="font-display text-4xl font-black tracking-tighter text-primary mb-8">Properties you <span className="brand-gradient-text">saved</span></h1>
      {loading ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" aria-label="Loading saved properties">{[0, 1, 2].map((item) => <Skeleton key={item} className="h-96 rounded-2xl" />)}</div> :
        error ? (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-6 py-12 text-center" role="alert">
            <p className="font-bold text-foreground">Saved properties could not be loaded.</p>
            <p className="mt-1 text-sm text-muted-foreground">{error}</p>
            <Button type="button" variant="outline" className="mt-4" onClick={load}>Try again</Button>
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg py-16 text-center bg-background">
            {/* Animated gradient empty-state icon */}
            <span className="mx-auto mb-3 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500/15 to-rose-400/5 ring-1 ring-rose-500/15 text-rose-500 animate-float-soft">
              <Heart className="w-8 h-8"/>
            </span>
            <p className="text-muted-foreground">No saved properties yet. Tap the heart icon on any listing.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map(p => <PropertyCard key={p.property_id} property={p} onSave={remove} saved={true}/>)}
          </div>
        )
      }
    </div>
  );
}
